/**
 * Created by SH: 02/20/2020
 */

import { Helper } from "../utils/Helper";
import { Power3,Back,gsap } from "gsap";
import { ReelEngine } from "./engine/ReelEngine";
import { IEngineCallbacks } from "./engine/IEngine";
import { ReelBaseSymbol, ISymbolAnimationConf } from './symbol/ReelBaseSymbol';
import { ReelEngineView, ReelState } from "./ReelEngineView";
import { ReelSymbolStates } from "./symbol/ReelSpriteSymbolArea";
import { GameSetting } from "../setting/GameSetting";
import { Logger } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { BasicSymbolType } from "./symbol/config/IReelSymbolStruct";
import { IReelConfig, ReelStartCallbacks, ReelStopCallbacks } from "./IData/IReelData";
import { ArrayHelper } from "../utils/ArrayHelper";
import { SymbolData } from "./symbol/config/SymbolData";
import { MathHelper } from "../utils/MathHelper";

export class ReelEngineController extends ReelEngineView {

    protected stopCallbacks:ReelStopCallbacks = {};
    protected startCallbacks:ReelStartCallbacks = {};

    public get playTimeLeft(): number { return this.engine.timeLeft; }
    public get nextPlayDuration(): number { return this.engine.nextPlayDuration;}

    setupFromRight:boolean = false;
    updateStep: boolean = false;

    protected defineQuickReel(data: any): string[] { return Array.from(data.reels[this.reelID]); }
    protected reelActiveNextSpin?(data: any):boolean;

    protected engineCallbacks(): IEngineCallbacks {
        return {
            onStartCallBack: () =>  this.onStarting(),
            onCompletedStepCallback: () => this.onStep(),
            onStopCallback: () => this.onStopStarted(),
            onCompletedStopCallback: () => this.onStopAnimationCompleted(),
            onCompletedStartCallback: () => this.onStartCompleted(),
            onCompletedExpanding: () => this.completedExpanding(),
            onNudgedCallback: () => this.onNudged(),
            onCompletedNudgeCallback: () => this.completedNudge(),
            onBounceStartCallback: () => this.onBounceStarted(),
        };
    }

    constructor(_ID:number,_config:IReelConfig , initData:any){
        super(_ID, _config);

        this.parseData(initData)
        this.createReelEngine();
        this.initReel();
    }

    public parseData(data:any): void {

        this._stopReelSet = Array.from(data.reels[this.reelID]);

        this._quickStopReelSet = this.defineQuickReel(data);

        this._nextReelSetName = data.reelsNextSpin;

        this._activeForNextSpin = this.reelActiveNextSpin ? this.reelActiveNextSpin(data) : true;

        this.setupFromRight = data.setupMode && data.isCarOnReel(4);
    };

    protected createReelEngine(): void {
        this._engine = new ReelEngine(this.engineCallbacks(), this._animationContainer);
        this._engine.stepDistance = this.currentStepDistance;
    }

    // Create maxAmount of symbols that will be seen & used.
    protected initReel(): void {
        this.setCurrentReelSet(this._nextReelSetName);
        
        let possAdded: number = 0;
        let symInitPos: number = this._stopReelSet.length;
        for (var i = -this.overflowAmount; i < (this.config.WINDOW_AMOUNT + this.overflowAmount); i++) {
            let symName: string = this._stopReelSet[symInitPos % this._stopReelSet.length];
            if (!symName || symName == BasicSymbolType.EMPTY) symName = "L1";
            const symbol: ReelBaseSymbol = this.createNewSymbol(i, symName);

            if(i == -this.overflowAmount && this.overflowAmount){
                let overflowAmount:number = (symbol.symbolHeight - this.engine.stepDistance)*0.5;
                possAdded = (symbol.symbolHeight * i) + overflowAmount;
                symbol.setPosID(-symbol.stackedValue);
                i += symbol.stackedValue - 1;
            }else if(this.AllSymbols.length){
                symbol.setPosID(ArrayHelper.fetchLastValue(this.AllSymbols[this.AllSymbols.length-1].posID) + 1)
                possAdded += Math.abs(symbol.symbolHeight)  * 0.5;
            }

            symbol.position = new PIXI.Point(0, possAdded);

            possAdded += (Math.abs(symbol.symbolHeight)) * 0.5;
            
            this.addSymbol(symbol);
            symInitPos++;
        }

        this.setVisibleForOverflowSymbols(false);
        this.addAndRemoveOutOfBound();
        this.sortChildren();

        this._hasInit = true;
    }

    public createNextAnimation(animationFunc:() => gsap.core.Timeline, newState?: string): gsap.core.Timeline {
        if (newState) this.state.set(newState);
        return animationFunc();
    }

    public createStart(cb?:ReelStartCallbacks, reverse: boolean = false): void {
        this.startCallbacks = cb ? cb : {};

        this.engine.reverse = reverse;

        if (this.isActive) {
            this.createNextAnimation(() => this.engine.Start(this.startDuration, this.startEase), ReelState.START);
        }
        else {
            this.createNextAnimation(() => this.engine.Idle(), ReelState.INACTIVE);
        }
    }

    public startReel(): void {
        if (this.state.isSpinning) return;
        if (!this.isActive) {
            if (this.visible) {
                this.engine.activateNextAnimation();
                this.createNextAnimation(() => this.engine.Idle(), ReelState.IDLE);
            }
            return;
        }

        this.setVisibleForOverflowSymbols(false);

        // Activate the Start-Animation.
        this.engine.activateNextAnimation();
        
        // Prepare Spin/Step-Animation.
        this.createNextAnimation(() => this.engine.Step(this.spinDuration));
    }

    protected onStarting(): void {

        Helper.triggerIfExist(this.startCallbacks.start);

        for (let index = 0; index < this.AllSymbols.length; index++) {
            this.AllSymbols[index].y = Math.round(this.AllSymbols[index].y);
        }
        
        for (var i = 0; i < this.AllSymbols.length; i++) {
            this.tryChangeState(this.AllSymbols[i],{
                state: ReelSymbolStates.SPIN,
                duration: this.startDuration,
                ease: this.startEase
            })
        }
    }

    protected tryChangeState(sym:ReelBaseSymbol, conf:ISymbolAnimationConf){
        if(sym.changeState) sym.changeState(conf);
    }

    protected onStartCompleted(): void {
        this.onStep();

        this.state.set(ReelState.STEP);

        // If Step Update happened before the spin.
        this.updateStep ? this.tryUpdateStep() : this.engine.activateNextAnimation();

        Helper.triggerIfExist(this.startCallbacks.started);
    }

    protected onStep(movedDistance:number = this.engine.stepDistance): void {

        this.AllSymbols.forEach(sym => {
            this.engine.reverse ? sym.setPosID(sym.posID[0] - 1) : sym.setPosID(sym.posID[0] + 1);
            sym.y += movedDistance;
        });

        this.addAndRemoveOutOfBound();

        // If Step Update happened during the spin.
        if(this.state.equal(ReelState.STEP)) this.tryUpdateStep();
    }

    protected tryUpdateStep():void{
        if(this.updateStep){

            this.engine.stepDistance = SymbolData.DEFAULT_DIM.HEIGHT;
            this.createNextAnimation(() => this.engine.Step(this.spinDuration));
            this.engine.activateNextAnimation();

            this.updateStep = false;
        }
    }

    setGraphAlpha(_alpha:number,_duration:number,_ease?:any):gsap.core.Timeline{
        if(!this.isActive) return new gsap.core.Timeline();
        this._graphTl = Helper.setTimeline(this._graphTl,new gsap.core.Timeline({
            onStart:() => {this._graphArea.visible = true},
            onComplete:() => {this._graphArea.visible = !!this._graphArea.alpha}
        }));
        this._graphTl.to(this._graphArea,{alpha:_alpha, duration: _duration, ease:_ease});
        
        return this._graphTl;
    }

    public activateStop(state:string):void{
        this.state.set(state);

        const reelStopReelResult: string[] = this._stopReelSet;

        const total:number = this.overflowAmount * 2 + this.amountOfStopSymbols;
        const steps:number = this.overflowAmount + this.amountOfStopSymbols;

        const remValue:number = reelStopReelResult.length - total;
        for(let k:number = 0; k < remValue;k++) reelStopReelResult.pop();

        const moveDistance:number = total*this.engine.stepDistance;

        const isReverse:boolean = this.engine.reverse;
        this.AllSymbols.forEach(s => {
            isReverse ? s.setPosID(s.posID[0]-total) : s.setPosID(s.posID[0]+total);
            s.position.y += moveDistance;
            s.position.y = Math.round(s.position.y);
        });

        const startSym:ReelBaseSymbol = isReverse ? this.bottomSymbol : this.topSymbol;
        let posOffset:number = startSym.y + ((isReverse ? 1 : -1) * ( startSym.symbolHeight*0.5 ));
        let nextSymindx:number = isReverse ? 0 : steps;

        let skipSteps:number = 0;
        let symbol: ReelBaseSymbol;

        for (var i = 0; i < total; i++) {
            const nextSymName: string = reelStopReelResult[nextSymindx] != undefined ? reelStopReelResult[nextSymindx] : this.generateNextSymbol();
            
            if(skipSteps > 0){
                
                if(nextSymName === reelStopReelResult[nextSymindx + (isReverse ? -1 : 1)]){
                    nextSymindx += isReverse ? 1 : -1;
                }else{ // if the scatter is cutted from top or bottom, add offset.
                    posOffset += skipSteps * (isReverse ? -this.engine.stepDistance : this.engine.stepDistance);
                    symbol!.position.y += (isReverse ? -this.engine.stepDistance : this.engine.stepDistance) * skipSteps;
                    symbol!.setPosID(symbol!.posID[0]  + (isReverse ?  -skipSteps : skipSteps))
                    skipSteps = 0;
                }
                skipSteps--;
                continue;
            }

            symbol = this.createNewSymbol(nextSymindx-1, nextSymName, ReelSymbolStates.SPIN);

            if(symbol.isStacked && !isReverse) symbol.setPosID(nextSymindx - symbol.posID.length)
            
            posOffset += (isReverse ? symbol.symbolHeight : -symbol.symbolHeight)*0.5;

            symbol.position = new PIXI.Point(0, posOffset);
            this.addSymbol(symbol);

            skipSteps = symbol.posID.length-1;
            nextSymindx += isReverse ? 1 : -1
            posOffset += (isReverse ? symbol.symbolHeight : -symbol.symbolHeight)*0.5;
        }

        this.engine.activateNextAnimation(-moveDistance + this._animationContainer.y);
    };
    
    public createStop(cb?:ReelStopCallbacks): gsap.core.Timeline {
        this.stopCallbacks = cb ? cb : {};
        return this.createNextAnimation(() => this.engine.Stop(this.stopDuration, this.stopEase) );
    }

    public stopSpin(): void {

        if (this.state.isSpinning) {
            Logger.logDev(`stop reel ${this.reelID} ${this._stopReelSet}`);

            this.activateStop(this.isQuickStop ? ReelState.QUICK_STOP : GameSetting.isFastSpin ? ReelState.FAST_STOP : ReelState.STOP);

        } else if ((!this.isActive) && this.state.equal(ReelState.IDLE) || this.state.equal(ReelState.INACTIVE)) {
            // If reel not active we fake a spin stopped
            Logger.logDev(`ReelEngineController.stopSpin():Inactive reel ${this.reelID} stop spin animation completed!`);
            this.onStopAnimationCompleted();
        }
    }

    protected onStopStarted(): void {
        for (var i = 0; i < this.AllSymbols.length; i++) {
            this.tryChangeState(this.AllSymbols[i],{
                state: ReelSymbolStates.NORMAL,
                duration: this.stopDuration,
                ease: Power3.easeInOut
            })
        }
        Helper.triggerIfExist(this.stopCallbacks.stops);
    }

    protected onBounceStarted(): void {
        this.setVisibleForOverflowSymbols(false);
        Helper.triggerIfExist(this.stopCallbacks.bounce);
    }

    protected onStopAnimationCompleted(): void {
        this.setCurrentReelSet(this._nextReelSetName);

        this.createNextAnimation(() => this.engine.Idle() , ReelState.IDLE);

        // Hide the overflowSymbols
        this.addAndRemoveOutOfBound();

        this.engine.activateNextAnimation();

        this._activeReel = this._activeForNextSpin;



        Helper.triggerIfExist(this.stopCallbacks.stopped);
    }

    protected completedNudge(): void {
        
        this.addAndRemoveOutOfBound();

        this.setVisibleForOverflowSymbols(false);

        this.createNextAnimation(() => this.engine.Idle(), ReelState.IDLE);

        this.engine.activateNextAnimation();
    }

    protected onNudged(): void {
        this.AllSymbols.forEach(sym => sym.y += this.engine.stepDistance );
    }
    
    public nudge(repeat:number = 0,durr:number = 0.5,reverse?:boolean, ease:any = Back.easeOut.config(0.7), addSymbol:string[] = [this.generateNextSymbol()],posChange?:number): void {

        if(this.state.isNudging) this.engine.activeTimeline?.totalProgress(1);

        this.setVisibleForOverflowSymbols(false);

        this.engine.reverse = !!reverse;

        this.createNextAnimation(() => this.engine.Nudge(repeat, durr, ease) , ReelState.NUDGE);

        this.addNudgeSymbol(this.engine.reverse, addSymbol);

        const posOffsetChange:number =  posChange == undefined ? this.allSymbolAmount - this.symbolConfAmount : posChange + 1;
        this.AllSymbols.forEach(sym => this.engine.reverse ? sym.setPosID(sym.posID[0] - posOffsetChange) : sym.setPosID(sym.posID[0] + posOffsetChange) );

        this.engine.activateNextAnimation();
    }

    protected addNudgeSymbol(reverse: boolean,symbolTypes:string[]): void {

        for(let i = 0; i < symbolTypes.length;i++){
            const symbolToCheck: ReelBaseSymbol = reverse ? this.bottomSymbol : this.topSymbol;
            let posY: number = symbolToCheck.position.y + ((reverse ? 1 : -1) * ( symbolToCheck.symbolHeight*0.5 ));
            // Check if it is visible by Restriction and symbol size.
            const nextSymName: string = symbolTypes[i];
            const posID: number = reverse ? Math.max(...symbolToCheck.posID) + 1 : Math.min(...symbolToCheck.posID) - 1;

            let symState: string = ReelSymbolStates.NORMAL;
            if (this.state.isSpinning || this.state.isStarting || this.state.isStopping) symState = ReelSymbolStates.SPIN;

            const symbol: ReelBaseSymbol = this.addingNextStepSymbol(posID, nextSymName, symState);
            posY += (reverse ? symbol.symbolHeight : -symbol.symbolHeight)*0.5;
            symbol.position.y = posY;
            this.addSymbol(symbol);
        }
    }

    public changeReelAmount(newSize: number): void {
        if(this.config.WINDOW_AMOUNT === newSize) return;

        this.config.WINDOW_AMOUNT = newSize;
        this.updateStep = true;
    }

    protected completedExpanding(): void {
        this.createNextAnimation(() => this.engine.Idle() , ReelState.IDLE);
    }

    protected setCurrentReelSet(name: string): void {
        if(!this.config.REEL_SETS.get(name)) throw (`Missing reelset: " + ${name}`);

        this._currentReelSet = this.config.REEL_SETS.get(name)![this.reelID];
    }

    ReelPosX(step:number):number{
        return ArrayHelper.fetchArrTypeValue(this.config.DIM.WIDTH,step) * 0.5 + (ArrayHelper.fetchArrTypeValue(this.config.DIM.PADDING_X,step) + ArrayHelper.fetchArrTypeValue(this.config.DIM.WIDTH,step)) * step;
    }

    ReelPosY(step:number):number{
        return ArrayHelper.fetchArrTypeValue(this.config.DIM.HEIGHT,step) * 0.5 + (ArrayHelper.fetchArrTypeValue(this.config.DIM.PADDING_Y,step) + ArrayHelper.fetchArrTypeValue(this.config.DIM.HEIGHT,step)) * step;
    }

    ReelPos(stepX:number, stepY:number):PIXI.Point{
        return new PIXI.Point(this.ReelPosX(stepX), 0);
    }

    public symPos(symID:number):PIXI.Point{
        return MathHelper.pointAdd(this.getSymbol(symID)!.position,this.position);
    }

    onResize():void{
        this.position = this.ReelPos(this.reelID,0)
    }
}
